import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";

interface CashEntryContextType {
  hasEnteredStartAmount: boolean;
  setHasEnteredStartAmount: (value: boolean) => void;
  isStartModalOpen: boolean;
  openStartModal: (options?: {
    isMandatory?: boolean;
    isSkipAllowed?: boolean;
  }) => void;
  closeStartModal: () => void;
  isSkipAllowed: boolean;
  isMandatory: boolean;
}

const CashEntryContext = createContext<CashEntryContextType | undefined>(
  undefined,
);

export const CashEntryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [hasEnteredStartAmount, setHasEnteredStartAmount] = useState(false);
  const [isStartModalOpen, setStartModalOpen] = useState(false);
  const [isSkipAllowed, setSkipAllowed] = useState(true);
  const [isMandatory, setMandatory] = useState(false);

  useEffect(() => {
    setHasEnteredStartAmount(false);
    setStartModalOpen(false);
    setSkipAllowed(true);
    setMandatory(false);
  }, [user?.id]);

  const openStartModal = useCallback(
    (options: { isMandatory?: boolean; isSkipAllowed?: boolean } = {}) => {
      setSkipAllowed(options.isSkipAllowed !== false);
      setMandatory(options.isMandatory === true);
      setStartModalOpen(true);
    },
    [],
  );

  const closeStartModal = useCallback(() => {
    setStartModalOpen(false);
    // Reset mandatory flag when closing
    setMandatory(false);
  }, []);

  return (
    <CashEntryContext.Provider
      value={{
        hasEnteredStartAmount,
        setHasEnteredStartAmount,
        isStartModalOpen,
        openStartModal,
        closeStartModal,
        isSkipAllowed,
        isMandatory,
      }}
    >
      {children}
    </CashEntryContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCashEntry = () => {
  const context = useContext(CashEntryContext);
  if (context === undefined) {
    throw new Error("useCashEntry must be used within a CashEntryProvider");
  }
  return context;
};
